import { Icon } from "@iconify/react";
import { useTranslation } from "react-i18next";
import type { FormTemplate, ProcessTemplate } from "@/services/template";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";

interface TemplateCardProps {
  template: FormTemplate | ProcessTemplate;
  type: "form" | "process";
  isPublisher: boolean;
  onUse: () => void;
  onDelete: () => void;
}

export const TemplateCard: React.FC<TemplateCardProps> = ({
  template,
  type,
  isPublisher,
  onUse,
  onDelete,
}) => {
  const { t } = useTranslation();

  return (
    <Card className="flex flex-col">
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between gap-2">
          <CardTitle className="text-base leading-snug">{template.name}</CardTitle>
          {template.category && (
            <Badge variant="secondary" className="shrink-0 text-xs">
              {template.category}
            </Badge>
          )}
        </div>
      </CardHeader>

      <CardContent className="flex-1 pb-2">
        {template.description ? (
          <p className="text-sm text-slate-500 line-clamp-3">{template.description}</p>
        ) : (
          <p className="text-sm text-slate-400 italic">
            {type === "form" ? t("templates.formTemplates") : t("templates.processTemplates")}
          </p>
        )}
      </CardContent>

      <CardFooter className="flex gap-2 pt-2">
        {isPublisher ? (
          <Button
            variant="destructive"
            size="sm"
            className="w-full"
            onClick={onDelete}
          >
            <Icon icon="tabler:trash" className="mr-1 h-4 w-4" />
            Delete
          </Button>
        ) : (
          <Button size="sm" className="w-full" onClick={onUse}>
            <Icon icon="tabler:copy" className="mr-1 h-4 w-4" />
            {t("templates.useTemplate")}
          </Button>
        )}
      </CardFooter>
    </Card>
  );
};
